import React from "react";
import { Ban, Flag, Heart, HeartHandshake, MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky action rail on a sponsor profile: request/message/favorite actions,
 * connection status, safety actions, and the self-view variant.
 *
 * Extracted verbatim from src/pages/SponsorProfile.jsx during the code-quality
 * pass: markup, copy, data-testids and behaviour are unchanged.
 */
export default function SponsorActionRail({
  sponsor,
  isSelf,
  navigate,
  requireAuth,
  onOpenRequest,
  onStartMessage,
  onToggleFavorite,
  onOpenReport,
  onOpenBlock,
}) {
  return (
    <div className="space-y-4">
      <div className="sm-card p-5 bg-white lg:sticky lg:top-28">
        {isSelf ? (
          <>
            <p className="text-sm text-[#4B5A60]">This is your sponsor profile.</p>
            <Button
              onClick={() => navigate("/become-a-sponsor")}
              className="mt-3 w-full bg-[#1F6F78] hover:bg-[#185B62] text-white"
            >
              Edit my profile
            </Button>
          </>
        ) : (
          <>
            {sponsor.connection_status === "pending" && (
              <p
                data-testid="connection-status-pending"
                className="mb-3 text-sm font-semibold text-[#B7791F] bg-[#FBF0DC] rounded-lg px-3 py-2"
              >
                Your sponsorship request is waiting for a reply.
              </p>
            )}
            {sponsor.connection_status === "accepted" && (
              <p
                data-testid="connection-status-accepted"
                className="mb-3 text-sm font-semibold text-[#1F7A4D] bg-[#E4F3EA] rounded-lg px-3 py-2"
              >
                You are connected with this sponsor.
              </p>
            )}
            <Button
              onClick={() => (requireAuth() ? onOpenRequest() : null)}
              disabled={
                sponsor.connection_status === "pending" ||
                sponsor.connection_status === "accepted" ||
                sponsor.availability_status === "not_accepting"
              }
              data-testid="request-sponsorship-button"
              className="w-full h-11 bg-[#1F6F78] hover:bg-[#185B62] text-white"
            >
              <HeartHandshake className="w-4 h-4 mr-2" /> Request Sponsorship
            </Button>
            {sponsor.availability_status === "not_accepting" && (
              <p className="mt-2 text-xs text-[#B42318]">
                This member is not accepting new sponsees right now.
              </p>
            )}
            <Button
              onClick={onStartMessage}
              variant="outline"
              data-testid="send-message-button"
              className="mt-2 w-full h-11 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Send Message
            </Button>
            <Button
              onClick={onToggleFavorite}
              variant="ghost"
              data-testid="add-favorite-button"
              className="mt-2 w-full h-11 text-[#1F2A2E] hover:bg-[#F2EFE8]"
            >
              <Heart
                className={`w-4 h-4 mr-2 ${
                  sponsor.is_favorite ? "fill-[#D07A4A] text-[#D07A4A]" : ""
                }`}
              />
              {sponsor.is_favorite ? "Saved to favorites" : "Add to Favorites"}
            </Button>
            <div className="mt-4 pt-4 border-t border-[#E3DED4] space-y-1">
              <button
                onClick={() => (requireAuth() ? onOpenReport() : null)}
                data-testid="report-profile-button"
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[#B42318] hover:bg-[#FBE6E4]"
              >
                <Flag className="w-4 h-4" /> Report Profile
              </button>
              <button
                onClick={() => (requireAuth() ? onOpenBlock() : null)}
                data-testid="block-profile-button"
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[#4B5A60] hover:bg-[#F2EFE8]"
              >
                <Ban className="w-4 h-4" /> Block this member
              </button>
            </div>
          </>
        )}
        <p className="mt-4 flex items-start gap-2 text-xs text-[#7A868B] leading-5">
          <ShieldCheck className="w-4 h-4 shrink-0 text-[#1F6F78]" />
          Blocking, reporting and emergency resources are always free and never require a
          membership.
        </p>
      </div>
    </div>
  );
}
